import { formatDate } from 'lib/helpers/dateUtils';
import { getImageFileId } from 'lib/helpers/exportPdf/pdfHeader';
import { filestackPath, getInitials, iaRa, removeHtmlTags } from 'lib/helpers/utility';

import { formatLabel, getArticle, getCreatorDisplayName } from '../../../../lib/helpers';
import { ACTION_MAP } from '../constants';
import { formatChangedValues } from '../formatChangedValues';

// Helper to clean text for PDF (remove HTML, normalize whitespace)
const cleanText = (text) => {
  if (
    text === null ||
    text === undefined ||
    text === 'undefined' ||
    text === '' ||
    text === 'blank' ||
    text === 'null'
  ) {
    return text;
  }

  let cleaned = removeHtmlTags(String(text), { preserveSpacing: false });
  cleaned = cleaned.replace(/\n/g, ' ');
  cleaned = cleaned.replace(/\s+/g, ' ');
  return cleaned.trim();
};

const getAvatarImagePath = (profilePhoto) => {
  if (!profilePhoto) {
    return;
  }

  const finalImageId = getImageFileId(profilePhoto);

  if (!finalImageId) {
    return;
  }

  const img = filestackPath(finalImageId);

  return img;
};

const getAvatarDetails = (creator, fullName = 'Unknown') => {
  const avatarImage = getAvatarImagePath(creator?.profile_photo);

  return {
    avatarImage,
    avatarInitials: getInitials(fullName || 'Unknown')
  };
};

const getFullName = (creator, isViewerAdmin, activity = {}) =>
  getCreatorDisplayName({
    creator,
    isAdminAction: activity.created_by_type === 0,
    isViewerAdmin
  });

const formatActivityForPdf = ({ item, category, timezone = null, isViewerAdmin = false }) => {
  const { creator, created_at, prefix = '', formattedChanges = [], isNestedCreate, action } = item;
  const fullName = getFullName(creator, isViewerAdmin, item);
  const { avatarImage, avatarInitials } = getAvatarDetails(creator, fullName);
  const time = formatDate(created_at, timezone, 'h:mma');

  const isCreateRecord = action === 'create';
  const isLifecycleEvent = ['cancel', 'reschedule', 'no_show', 'restore'].includes(action);

  const actionText = ACTION_MAP[isNestedCreate ? 'create' : action] || 'updated';
  let authorText;

  if (isCreateRecord && (item.entityDetail || item.appointmentLink)) {
    const entityLabel = prefix || category || 'record';
    authorText = `${fullName} ${actionText} a new ${entityLabel}`;
  } else if (isCreateRecord) {
    const entityLabel = prefix || category || 'record';
    authorText = `${fullName} ${actionText} the ${entityLabel}`;
  } else if (isLifecycleEvent) {
    const entityLabel = prefix || category || 'record';
    authorText = `${fullName} ${actionText} ${getArticle(entityLabel)} ${entityLabel}`;
  } else {
    authorText = `${fullName} ${actionText}${prefix ? ` ${prefix}` : ''}`;
  }

  const changes = [];
  const isDelete = action === 'delete';

  if (isCreateRecord || isLifecycleEvent) {
    const detailParts = [];
    if (item.appointmentLink) detailParts.push(`#${item.appointmentLink}`);
    if (item.appointmentDate) {
      detailParts.push(formatDate(item.appointmentDate, timezone, 'MMM d, yyyy h:mma'));
    }
    if (item.appointmentProvider) detailParts.push(item.appointmentProvider);
    if (item.entityDetail) detailParts.push(item.entityDetail);

    const detailLine = detailParts.length > 0 ? [detailParts.join(' · ')] : [];
    return { time, changes: detailLine, authorText, avatarImage, avatarInitials };
  }

  formattedChanges.forEach((item) => {
    const { key, isNestedKey, prevValueRaw, currValueRaw } = item;

    const label = formatLabel(key, isNestedKey);

    if (isNestedKey) {
      const formattedResults = formatChangedValues({
        activities: [{ previousValues: prevValueRaw, currentValues: currValueRaw }]
      });
      const formattedChanges = formattedResults[0]?.formattedChanges || [];

      const { changes: nestedChanges } = formatActivityForPdf({ item: { formattedChanges } });

      changes.push({ type: 'update', label, changes: nestedChanges });
      return;
    }

    const prevValue = cleanText(item.prevValue) || 'blank';
    const currValue = cleanText(item.currValue) || 'blank';

    if (prevValue !== currValue) {
      if (isDelete) {
        // For deletes, show what was removed
        changes.push({ type: 'delete', label, prevValue });
      } else {
        // For updates, show from → to
        changes.push({ type: 'update', label, prevValue, currValue });
      }
    }
  });

  return { time, authorText, changes, avatarImage, avatarInitials };
};

const formatCommentForPdf = (comment, isReply = false, timezone = null, isViewerAdmin = false) => {
  const { creator, created_at, updated_at, value } = comment;
  const fullName = getFullName(creator, isViewerAdmin, comment);
  const { avatarImage, avatarInitials } = getAvatarDetails(creator, fullName);
  const time = formatDate(created_at, timezone, 'h:mma');
  const commentText = cleanText(value || '');
  const isEdited = updated_at && created_at !== updated_at;

  return {
    time,
    authorText: `${fullName} ${isReply ? 'replied' : 'commented'}:`,
    changes: [commentText ? `${commentText}${isEdited ? ' (edited)' : ''}` : 'Comment: (empty)'],
    isReply,
    avatarImage,
    avatarInitials
  };
};

export const buildFormattedActivities = ({
  groupedActivities,
  category,
  isViewerAdmin = false,
  timezone = null
}) => {
  if (!groupedActivities || groupedActivities.dateKeys.length === 0) {
    return [];
  }

  const formattedActivities = [];

  groupedActivities.dateKeys.forEach((dateKey) => {
    const activities = groupedActivities.grouped[dateKey];

    formattedActivities.push({ dateKey });

    iaRa(activities).forEach((item) => {
      if (item.type === 'comment') {
        const replies = iaRa(item.replies);

        // Add the main comment
        formattedActivities.push({
          ...formatCommentForPdf(item, false, timezone, isViewerAdmin),
          isReplyNext: replies.length > 0
        });

        // Add replies if they exist (they share the parent's activity index)
        replies.forEach((reply, replyIndex) => {
          formattedActivities.push({
            ...formatCommentForPdf(reply, true, timezone, isViewerAdmin),
            isReplyNext: replyIndex < replies.length - 1
          });
        });
      } else {
        formattedActivities.push(formatActivityForPdf({ item, category, timezone, isViewerAdmin }));
      }
    });
  });

  return formattedActivities;
};
